import { Component, inject } from '@angular/core';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { OrderModel } from '../../../../model/order.model';
import { catchError, EMPTY, filter, finalize, switchMap, tap } from 'rxjs';
import { StoreService } from '../../../../services/store.service';
import { OrdersService } from '../../../../services/orders.service';
import { ORDER_STATUS } from '../../../../config/order.config';
import { canCancelOrder } from '../../../../utils/order.utils';
import { DialogService } from '../../../../services/dialog.service';
import { MatDialog } from '@angular/material/dialog';
import { OrderUsersDialogComponent } from '../order-users-dialog/order-users-dialog.component';
import { TasksService } from "../../../../modules/go/services/tasks.service";

@Component({
  selector: 'app-orders-grid-actions',
  templateUrl: './orders-grid-actions.component.html',
})
export class OrdersGridActionsComponent  implements ICellRendererAngularComp {
  private readonly tasksService = inject(TasksService);
  private readonly store = inject(StoreService);
  private readonly ordersService = inject(OrdersService);
  private readonly dialogService = inject(DialogService);
  private readonly dialog = inject(MatDialog);

  private params!: ICellRendererParams;
  private isInternal = false;

  protected cellValue!: OrderModel;
  protected canDownloadZip = false;
  protected canAddUsers = false;
  protected canClose = false;
  protected canViewOrderCard = false;

  public refresh(params: ICellRendererParams): boolean {
    this.cellValue = params.data;
    return true;
  }

  public agInit(params: ICellRendererParams) {
    this.params = params;
    this.cellValue = params.data;
    this.isInternal = this.cellValue.source === 'ENPROM';
    this.canDownloadZip = this.cellValue.status === ORDER_STATUS.CLOSED;
    this.canAddUsers = this.cellValue.status === 'IN_PROGRESS';
    this.canClose = this.cellValue.canClose && this.store.canCloseOrders() && this.isInternal;
    // this.canViewOrderCard = this.cellValue.cardState !== 'UNSET';
    // this.canViewOrderCard = true;
  }

  protected cancel(): void {
    this.dialogService.promptForCancelAction().afterClosed().pipe(
      filter(result => result),
      tap(() => this.store.showLoader()),
      switchMap(() => this.ordersService.cancelOrder(this.cellValue.id)),
      tap(() => {
        this.store.showSuccessMsg('ORDERS.CANCEL_SUCCESS');
        this.store.refreshOrders();
      })
    ).subscribe();
  }

  protected run() {
    this.store.showLoader();
    this.ordersService.runOrder(this.cellValue.id).pipe(
      tap(() => {
        this.store.showSuccessMsg('ORDERS.RUN_SUCCESS')
        this.store.refreshOrders();
      })
    ).subscribe();
  }

  protected downloadZip() {
    this.store.downloadOrderZip(this.cellValue.id);
  }

  protected close() {
    this.dialogService.promptForCloseOrder(this.cellValue.name).afterClosed().pipe(
      filter(result => result),
      tap(() => this.store.showLoader()),
      switchMap(() => this.ordersService.close(this.cellValue.id)),
      tap(() => {
        this.store.showSuccessMsg('ORDERS.CLOSE_SUCCESS')
        this.store.refreshOrders();
        this.tasksService.refreshTasks();
      }),
      catchError((error) => {
        if (error.error.code === 'ORDER_FORCE_CLOSE_BLOCKED') {
          this.store.refreshOrders();
        }

        return EMPTY;
      })
    ).subscribe();
  }

  protected canBeCancel() {
    return canCancelOrder(this.cellValue, this.store.isAuthor(this.cellValue.createdBy.id));
  }

  protected addUsers() {
    const assignedUsersIds = new Set(this.cellValue.assignedTo.map(item => item.id));
    this.store.showLoader();
    this.ordersService.getConfigUsers(this.store.selectedProject.id).pipe(
      finalize(() => this.store.hideLoader())
    ).subscribe({
      next: (users) => {
        const availableUsers = users.filter(user => !assignedUsersIds.has(user.id));

        if (availableUsers.length === 0) {
          this.dialogService.showInfoDialog({
            content: 'ORDERS.NO_AVAILABLE_INSPECTORS',
            header: 'ORDER',
            hideCancelBtn: true,
            confirmBtn: 'OK',
          });
          return;
        }

        this.dialog.open(OrderUsersDialogComponent, {
          data: {
            users: availableUsers,
            orderId: this.cellValue.id,
          },
        });
      }
    });
  }

  protected showOrderCardDialog() {
    this.dialogService.showOrderCardDialog();
  }
}
